import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

const STAGES = [
  {
    n: "01",
    t: "Cierge Discovery",
    p: "One bounded problem and one consequential handoff.",
    o: "Role Network Blueprint · evidence boundary · authority matrix",
  },
  {
    n: "02",
    t: "Cierge Lab Sandbox",
    p: "A synthetic but realistic governed journey.",
    o: "Sponsor-facing demonstrator · receipt model · acceptance criteria",
  },
  {
    n: "03",
    t: "Multi-Role Demonstrator",
    p: "Separate role identities and devices.",
    o: "Role-node configurations · R2R protocol prototype · cross-device proof",
  },
  {
    n: "04",
    t: "Role Network Pilot",
    p: "A controlled system-of-record boundary.",
    o: "Adapter · test harness · operational acceptance evidence",
  },
  {
    n: "05",
    t: "KATLAS Governance Licence",
    p: "A reusable runtime operating capability.",
    o: "Role-node licences · protocol packages · support and versioning",
  },
];

export default function CiergeLabs() {
  return (
    <div data-testid="cierge-studio-page">
      <PageIntro
        index="/cierge-studio"
        eyebrow="Cierge Design Studio"
        titleLines={["Design the journey.", "Govern the handoff.", "Prove the outcome."]}
        lead="The Cierge Design Studio is the applied journey discovery, design and pilot capability that turns one consequential cross-organisational handoff into a governed operational journey. We start with the role that owns the outcome — not a broad platform, dashboard or data model."
        meta={["Journey Storybook", "Governed Sandbox", "KATLAS licence"]}
      />

      <section className="border-b border-ink/12 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 md:grid-cols-3">
            {[
              { t: "Journey Storybook", d: "Explains a role-led scenario." },
              { t: "Governed Sandbox", d: "Proves the governed journey." },
              { t: "KATLAS Licence", d: "Operates the reusable governance substrate." },
            ].map((c) => (
              <RevealItem key={c.t} className="bg-stone p-8">
                <h3 className="font-serif text-xl text-ink">{c.t}</h3>
                <p className="mt-3 font-sans text-sm text-ink/70">{c.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 flex items-center gap-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">§ engagement</span>
            <span className="h-px w-10 bg-ink/25" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
              Discovery → Licence
            </span>
          </div>

          <div className="divide-y divide-ink/12 border-y border-ink/12">
            {STAGES.map((s) => (
              <Reveal key={s.n} className="grid grid-cols-1 gap-6 py-10 md:grid-cols-12 md:gap-10 md:py-12">
                <div className="md:col-span-1">
                  <span className="font-serif text-4xl text-ink/20 md:text-5xl">{s.n}</span>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-serif text-2xl text-ink md:text-3xl">{s.t}</h3>
                  <p className="mt-3 font-sans text-base text-ink/70">{s.p}</p>
                </div>
                <div className="md:col-span-6 md:pt-2">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                    Typical output
                  </p>
                  <p className="mt-2 font-mono text-sm text-ink/75">{s.o}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-16">
            <Link
              to="/company#contact"
              className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
            >
              Bring us a journey
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
